// Boss definitions. Phaser-free so the HUD, tests and tooling can read them.
// Choreography lives in src/systems/bosses/<boss>.ts; every number a designer
// would want to tune (hp, damage, telegraph wind-ups, pacing per phase) lives here.

import { COLORS } from '../config/palette';
import type { BiomeId, BossId } from '../core/types';

/** Seconds of the scripted entrance before the first attack. */
export const BOSS_INTRO_SEC = 1.2;
/** Seconds a boss is invulnerable while it changes phase. */
export const BOSS_TRANSITION_SEC = 1.2;
/** Seconds of the death sequence before `boss:defeated`. */
export const BOSS_DEATH_SEC = 2.5;

/** Pacing for one phase. Later phases are faster, denser and rest less. */
export interface BossPhaseTuning {
  /** Multiplier on attack motion (bullet speed, sweep speed, waves). */
  speed: number;
  /** Multiplier on telegraph wind-ups. Never below ~0.75 so tells stay readable. */
  tell: number;
  /** Seconds of movement between attacks inside a combo. */
  recovery: number;
  /** Seconds of breathing room after the vulnerability window. */
  rest: number;
  /** Attacks per combo (inclusive range) before the vulnerability window. */
  combo: readonly [number, number];
  /** Movement speed in px/s. */
  move: number;
}

export interface BossAttackDef<A extends string = string> {
  id: A;
  /** Relative pick weight (further biased by positioning at runtime). */
  weight: number;
  /** First phase (1-based) in which the attack can be picked. */
  minPhase: number;
  /** Seconds before the attack may be picked again. */
  cooldown: number;
}

export interface BossDef<A extends string = string, D extends string = string> {
  id: BossId;
  name: string;
  title: string;
  /** 0-based sector this boss guards. */
  sector: number;
  biome: BiomeId;
  /** Sector difficulty is already baked into these values. */
  maxHp: number;
  /** HP fractions at which phase 2, 3, ... begin. */
  phaseThresholds: readonly number[];
  scrapValue: number;
  /** Approximate radius for chain / area targeting. */
  radius: number;
  flying: boolean;
  /** Banner / HUD accent. */
  color: number;
  /** Emissive accent of the boss (weak points, eyes). */
  glow: number;
  phases: readonly BossPhaseTuning[];
  attacks: readonly BossAttackDef<A>[];
  /** Damage per hit, by hazard. */
  damage: Readonly<Record<D, number>>;
  /** Seconds the weak point stays exposed. */
  weakWindow: number;
  /** Damage multiplier of an exposed weak point. */
  weakMult: number;
}

// ───────────────────────────── WARDEN-7 ─────────────────────────────

export type WardenAttackId = 'sweep' | 'mortar' | 'stomp' | 'drones';
export type WardenDamageId = 'laser' | 'mortar' | 'wave' | 'stomp' | 'contact';

export const WARDEN: BossDef<WardenAttackId, WardenDamageId> = {
  id: 'warden',
  name: 'WARDEN-7',
  title: 'Graveyard Sentinel',
  sector: 0,
  biome: 'graveyard',
  maxHp: 2400,
  phaseThresholds: [0.66, 0.33],
  scrapValue: 90,
  radius: 120,
  flying: false,
  color: COLORS.threat,
  glow: COLORS.threat,
  phases: [
    { speed: 1, tell: 1, recovery: 1.25, rest: 1.6, combo: [2, 2], move: 78 },
    { speed: 1.14, tell: 0.9, recovery: 0.95, rest: 1.25, combo: [2, 3], move: 96 },
    { speed: 1.28, tell: 0.8, recovery: 0.7, rest: 0.95, combo: [3, 3], move: 118 },
  ],
  attacks: [
    { id: 'sweep', weight: 3, minPhase: 1, cooldown: 5 },
    { id: 'mortar', weight: 3, minPhase: 1, cooldown: 3.5 },
    { id: 'stomp', weight: 3, minPhase: 1, cooldown: 3 },
    { id: 'drones', weight: 2.2, minPhase: 2, cooldown: 16 },
  ],
  damage: { laser: 22, mortar: 18, wave: 16, stomp: 22, contact: 14 },
  weakWindow: 2.5,
  weakMult: 2,
};

// ───────────────────────────── FOUNDRY TITAN ─────────────────────────────

export type TitanAttackId = 'pour' | 'grapple' | 'slag' | 'hammer' | 'flame';
export type TitanDamageId = 'molten' | 'puddle' | 'grapple' | 'slag' | 'hammer' | 'wave' | 'flame' | 'contact';

export const FOUNDRY_TITAN: BossDef<TitanAttackId, TitanDamageId> = {
  id: 'foundry_titan',
  name: 'FOUNDRY TITAN',
  title: 'Crucible Overseer',
  sector: 1,
  biome: 'foundry',
  maxHp: 3400,
  phaseThresholds: [0.66, 0.33],
  scrapValue: 120,
  radius: 150,
  flying: true,
  color: COLORS.threatHot,
  glow: COLORS.threatHot,
  phases: [
    { speed: 1, tell: 1, recovery: 1.1, rest: 1.4, combo: [2, 2], move: 210 },
    { speed: 1.12, tell: 0.9, recovery: 0.85, rest: 1.15, combo: [2, 3], move: 250 },
    { speed: 1.25, tell: 0.8, recovery: 0.62, rest: 0.9, combo: [2, 3], move: 290 },
  ],
  attacks: [
    { id: 'pour', weight: 3, minPhase: 1, cooldown: 5 },
    { id: 'grapple', weight: 2.5, minPhase: 1, cooldown: 5 },
    { id: 'slag', weight: 3, minPhase: 1, cooldown: 3.5 },
    { id: 'hammer', weight: 3.2, minPhase: 1, cooldown: 4 },
    { id: 'flame', weight: 3.4, minPhase: 3, cooldown: 6 },
  ],
  damage: { molten: 20, puddle: 8, grapple: 14, slag: 13, hammer: 26, wave: 14, flame: 18, contact: 16 },
  weakWindow: 2.3,
  weakMult: 2,
};

// ───────────────────────────── SINGULARITY HEART ─────────────────────────────

export type HeartAttackId = 'well' | 'spiral' | 'cross' | 'blink';
export type HeartDamageId = 'horizon' | 'orb' | 'laser' | 'burst' | 'contact';

export const SINGULARITY_HEART: BossDef<HeartAttackId, HeartDamageId> = {
  id: 'singularity_heart',
  name: 'SINGULARITY HEART',
  title: 'The Unmade Core',
  sector: 2,
  biome: 'vault',
  maxHp: 4400,
  phaseThresholds: [0.66, 0.33],
  scrapValue: 150,
  radius: 100,
  flying: true,
  color: COLORS.orbUnstable,
  glow: COLORS.threat,
  phases: [
    { speed: 1, tell: 1, recovery: 1.05, rest: 1.2, combo: [2, 2], move: 120 },
    { speed: 1.12, tell: 0.9, recovery: 0.85, rest: 1, combo: [2, 3], move: 150 },
    { speed: 1.26, tell: 0.8, recovery: 0.65, rest: 0.8, combo: [3, 3], move: 180 },
  ],
  attacks: [
    { id: 'well', weight: 2.4, minPhase: 1, cooldown: 7 },
    { id: 'spiral', weight: 3, minPhase: 1, cooldown: 4 },
    { id: 'cross', weight: 3, minPhase: 1, cooldown: 4.5 },
    { id: 'blink', weight: 2.2, minPhase: 1, cooldown: 3 },
  ],
  damage: { horizon: 20, orb: 12, laser: 22, burst: 12, contact: 20 },
  weakWindow: 2.6,
  weakMult: 2,
};

export const BOSSES: Readonly<Record<BossId, BossDef>> = {
  warden: WARDEN,
  foundry_titan: FOUNDRY_TITAN,
  singularity_heart: SINGULARITY_HEART,
};

export const BOSS_ORDER: readonly BossId[] = ['warden', 'foundry_titan', 'singularity_heart'];
